import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Paper,
  Stack,
  Chip,
  IconButton,
  Card,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import ExpandMoreIcon from '@iconify/icons-eva/expand-outline';
import PlayCircleOutlineIcon from '@iconify/icons-eva/play-circle-outline';
import Page from '../components/Page';
import PageHeader from '../components/common/PageHeader';
import { BackButton } from '../common/components/BackButton';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const VideoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z2,
  border: `1px solid ${theme.palette.grey[200]}`,
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: '0 0 16px 0'
  },
  '& .MuiAccordionSummary-root': {
    padding: theme.spacing(2, 3),
    '&.Mui-expanded': {
      minHeight: 'auto',
      '& .MuiAccordionSummary-content': {
        margin: '12px 0'
      }
    }
  },
  '& .MuiAccordionDetails-root': {
    padding: theme.spacing(0, 3, 3, 3)
  }
}));

const ContactCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z2,
  border: `1px solid ${theme.palette.grey[200]}`,
  textAlign: 'center',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
}));

const FAQ_DATA = [
  {
    id: 1,
    question: "How do I start a transaction from my CMS portal?",
    answer: (
      <Box>
        <Typography variant="body1" paragraph>
          To start a transaction:
        </Typography>
        <Box component="ol" sx={{ pl: 2, mb: 2 }}>
          <li>Login to the CMS Portal.</li>
          <li>Navigate to the Team where the charger is located.</li>
          <li>Create an RFID card.</li>
          <li>Create an EV driver using the same email ID as your CMS login.</li>
          <li>Go to the Charger Details page → Remote Operations.</li>
          <li>Select Remote Start, choose the RFID Tag, and press Submit.</li>
        </Box>
        <Typography variant="body1" paragraph>
          This will trigger a remote start transaction using your RFID card.
        </Typography>
        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
            ➡️ For future transactions on the same team, simply:
          </Typography>
          <Typography variant="body2">
            Navigate to Charger Details, select Remote Start, pick the OCPP Tag, and press Submit.
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 2,
    question: "How do I stop a transaction?",
    answer: (
      <Box>
        <Box component="ol" sx={{ pl: 2, mb: 2 }}>
          <li>Login to the CMS Portal.</li>
          <li>Click on the Transactions menu (left side).</li>
          <li>Locate the active transaction (orange dot = active, green dot = completed).</li>
          <li>Open the Action Menu (two dots left of each transaction row).</li>
          <li>Select Stop Transaction.</li>
        </Box>
        <Box sx={{ bgcolor: 'info.50', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Refund Information:
          </Typography>
          <Typography variant="body2" component="div">
            • If started via UPI/App Payment, refunds are processed immediately (may take up to 48 hours to reflect).<br/>
            • If started via Wallet, money is refunded to the wallet.<br/>
            • If started via RFID, no money will be refunded.
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 3,
    question: "How do I check the transaction status?",
    answer: (
      <Box>
        <Box component="ol" sx={{ pl: 2, mb: 2 }}>
          <li>Login to the CMS Portal.</li>
          <li>Click on Transactions from the left menu.</li>
          <li>Review the transaction list:</li>
        </Box>
        <Box sx={{ pl: 4, mb: 2 }}>
          <Typography variant="body2" component="div">
            • <Chip label="Green icon" size="small" color="success" sx={{ mr: 1 }} /> = Completed transaction.<br/>
            • <Chip label="Orange icon" size="small" color="warning" sx={{ mr: 1 }} /> = Active transaction.
          </Typography>
        </Box>
        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            💡 Payment status:
          </Typography>
          <Typography variant="body2" component="div">
            • Wallet or RFID transactions will not have a Payment ID.<br/>
            • For completed transactions where all funds were used, "No Refund" will be displayed.
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 4,
    question: "How do I check the payment refund status?",
    answer: (
      <Box>
        <Box component="ol" sx={{ pl: 2, mb: 2 }}>
          <li>Login to the CMS Portal.</li>
          <li>Go to the Transactions menu → check for the Payment ID.</li>
          <li>If the transaction did not start, go to the Payments Tab.</li>
          <li>Search by UPI ID to view details and refund status.</li>
        </Box>
        <Box sx={{ bgcolor: 'primary.50', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            👉 Customers can also check directly at:
          </Typography>
          <Link 
            href="https://cpo.amiktech.in/check-transaction-status" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ color: 'primary.main', textDecoration: 'underline' }}
          >
            https://cpo.amiktech.in/check-transaction-status
          </Link>
        </Box>
      </Box>
    )
  },
  {
    id: 5,
    question: "A customer made a payment but the transaction did not start. How do I assist?",
    answer: (
      <Box>
        <Typography variant="body1" paragraph>
          Guide the customer to:
        </Typography>
        <Box sx={{ bgcolor: 'primary.50', p: 2, borderRadius: 1, mb: 2 }}>
          <Link 
            href="https://cpo.amiktech.in/check-transaction-status" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ color: 'primary.main', textDecoration: 'underline' }}
          >
            https://cpo.amiktech.in/check-transaction-status
          </Link>
        </Box>
        <Typography variant="body1" paragraph>
          As a CPO:
        </Typography>
        <Box component="ol" sx={{ pl: 2 }}>
          <li>Login to the CMS Portal.</li>
          <li>Go to Transactions → Payments Tab.</li>
          <li>Locate the Payment ID provided by the customer to view details.</li>
        </Box>
      </Box>
    )
  },
  {
    id: 6,
    question: "The transaction stopped suddenly. How do I check?",
    answer: (
      <Box component="ol" sx={{ pl: 2 }}>
        <li>Go to the Transactions page.</li>
        <li>Check if the transaction is Completed or Active.</li>
        <li>If completed, click the Transaction ID to view details.</li>
        <li>Look under the Reason Section for the stop reason.</li>
      </Box>
    )
  },
  {
    id: 7,
    question: "There is a connector fault status. What should I do?",
    answer: (
      <Box component="ol" sx={{ pl: 2 }}>
        <li>Navigate to the Transactions Page.</li>
        <li>Click on the Transaction Details.</li>
        <li>Select View Logs to analyze the issue.</li>
        <li>Check the Stop Reason field for fault information.</li>
      </Box>
    )
  },
  {
    id: 8,
    question: "I made a payment but the transaction did not start. How do I investigate?",
    answer: (
      <Box>
        <Box component="ol" sx={{ pl: 2, mb: 2 }}>
          <li>Navigate to Transactions in CMS.</li>
          <li>Locate the Payment ID.</li>
          <li>Search for a linked Transaction ID:</li>
        </Box>
        <Box sx={{ pl: 4, mb: 2 }}>
          <Typography variant="body2" component="div">
            • If found → The transaction stopped immediately. Check Transaction Logs for details.<br/>
            • If not found → The charger did not respond to the remote start. Review logs.
          </Typography>
        </Box>
        <Box sx={{ bgcolor: 'warning.50', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ⚡ Next steps: Consult the Hardware or Software team based on the log analysis.
          </Typography>
        </Box>
      </Box>
    )
  }
];

// ----------------------------------------------------------------------

export default function FAQ() {
  return (
    <Page title="FAQ">
      <BackButton
        text=""
        path="/dashboard"
      />
      
      <PageHeader 
        title="" 
        subtitle="Find answers to common questions about using the CMS Portal"
      />

      {/* FAQ Section */}
      <StyledCard sx={{ mb: 4 }}>
        <Box>
          {FAQ_DATA.map((faq, index) => (
            <StyledAccordion 
              key={faq.id} 
              defaultExpanded={index === 0}
            >
              <AccordionSummary
                expandIcon={<Icon icon={ExpandMoreIcon} width={24} height={24} />}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'left' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {faq.answer}
              </AccordionDetails>
            </StyledAccordion>
          ))}
        </Box>
      </StyledCard>

      {/* Video Tutorial Section */}
      <VideoCard 
        sx={{ 
          mb: 4,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          },
          transition: 'all 0.3s ease'
        }}
      >
        <Link
          href="https://www.youtube.com/playlist?list=PLNsi9ubaA0MAq1Ao97W_hY03zYkjTfn86"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'block',
            width: '100%'
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Icon icon={PlayCircleOutlineIcon} width={32} height={32} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Video Tutorials
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Watch our comprehensive video guides to learn how to use the CMS Portal effectively
                </Typography>
              </Box>
            </Stack>
            <Icon icon={PlayCircleOutlineIcon} width={24} height={24} />
          </Stack>
        </Link>
      </VideoCard>

      {/* Contact Support Section */}
      <ContactCard sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Still need help?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          If you can't find the answer you're looking for, please contact our support team.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Chip 
            label="Email Support" 
            variant="outlined" 
            clickable
            onClick={() => window.open('mailto:info@amiktech.in')}
          />
        </Stack>
      </ContactCard>
    </Page>
  );
}
